// src/components/CategoryTabs.tsx
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { useGeneralInfoStore } from '../stores/GeneralInfoStore';
import { useTranslatedTilesData } from '../config/tilesConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import appRoutesConfig from '../config/appConfig';
import { demoMode } from '../config/config';
import { useUserStore } from '../stores/userStore';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// ─── cookie helpers ────────────────────────────────────────────────────────────
function getCookie(name: string): string {
    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(name + '='));
    return match ? decodeURIComponent(match.split('=')[1]) : '';
}

function setCookie(name: string, value: string, days = 30, path = '/'): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    document.cookie = `${name}=${encodeURIComponent(
        value
    )};expires=${expires.toUTCString()};path=${path}`;
}

const CategoryTabs: React.FC = () => {
    const {
        category,
        activeTab,
        currentPage,
        setCategory,
        setActiveTab,
        setCurrentPage,
    } = useGeneralInfoStore();

    const translatedTiles = useTranslatedTilesData();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { user } = useUserStore();
    const userRoles: string[] = user?.roles || [];

    // Scroll button state
    const containerRef = useRef<HTMLDivElement>(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);



    // ─── Arrow visibility update function ────────────────────────────────────────
    const updateArrowsVisibility = () => {
        if (containerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
            setShowLeft(scrollLeft > 0);
            setShowRight(scrollLeft + clientWidth < scrollWidth - 10);
        }
    };

    // ─── 1. Seed store on mount from cookies ────────────────────────────────────
    useEffect(() => {
        const savedCategory = getCookie('category');
        if (savedCategory && savedCategory !== category) {
            setCategory(savedCategory);
        }

        const savedTab = getCookie('activeTab');
        if (savedTab && savedTab !== activeTab) {
            setActiveTab(savedTab);
        }

        const savedPage = getCookie('currentPage');
        if (savedPage && savedPage !== currentPage) {
            setCurrentPage(savedPage);
        }
    }, []); // run once on mount

    // ─── 2. Persist category whenever it changes ────────────────────────────────
    useEffect(() => {
        if (category) {
            setCookie('category', category);
        }
    }, [category]);

    // ─── 3. Persist activeTab whenever it changes ───────────────────────────────
    useEffect(() => {
        if (activeTab) {
            setCookie('activeTab', activeTab);
        }
    }, [activeTab]);

    // ─── 4. Persist currentPage whenever it changes ─────────────────────────────
    useEffect(() => {
        if (currentPage) {
            setCookie('currentPage', currentPage);
        }
    }, [currentPage]);

    // ─── Setup scroll listeners ──────────────────────────────────────────────────
    useEffect(() => {
        updateArrowsVisibility();
        window.addEventListener('resize', updateArrowsVisibility);
        return () => window.removeEventListener('resize', updateArrowsVisibility);
    }, []);

    // ─── Calculate filtered pages ────────────────────────────────────────────────
    const pagesForActiveTab = useMemo(() => {
        let pages;
        if (demoMode) {
            pages = translatedTiles.filter(
                (tile) =>
                    tile.categories.includes(category) &&
                    tile.tabs && tile.tabs.includes(activeTab)
            );
        } else {
            pages = translatedTiles.filter(
                (tile) => {
                    const hasCategory = tile.categories.includes(category);
                    const hasActiveTabInTile = !tile.tabs || tile.tabs.includes(activeTab);
                    const userHasRoleForTile = userRoles.includes(tile.id);

                    return hasCategory && hasActiveTabInTile && userHasRoleForTile;
                }
            );
        }

        // Sort pages so that "WeatherMonitoring" and "DownloadManager" are always the last 2 elements
        pages.sort((a, b) => {
            const isASpecial = a.id === 'WeatherMonitoring' || a.id === 'DownloadManager';
            const isBSpecial = b.id === 'WeatherMonitoring' || b.id === 'DownloadManager';

            // If both are special, put DownloadManager last
            if (isASpecial && isBSpecial) {
                if (a.id === 'DownloadManager') return 1;
                if (b.id === 'DownloadManager') return -1;
                return 0;
            }

            // If only one is special, put it at the end
            if (isASpecial) return 1;
            if (isBSpecial) return -1;

            // For non-special items, maintain original order
            return 0;
        });

        return pages;
    }, [translatedTiles, category, activeTab, userRoles]);


    // ─── Update arrow visibility when content changes ────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            updateArrowsVisibility();
        }, 50);

        return () => clearTimeout(timer);
    }, [pagesForActiveTab.length, category, activeTab]);

    // ─── Scroll by container width function ──────────────────────────────────────
    const scrollByContainerWidth = (direction: 'left' | 'right') => {
        if (containerRef.current) {
            const containerWidth = containerRef.current.clientWidth;
            const scrollAmount = containerWidth * 0.8; // Scroll 80% of container width for slight overlap
            containerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth',
            });
            setTimeout(() => updateArrowsVisibility(), 300);
        }
    };

    // ─── Disable tabs on landing/catalogue ───────────────────────────────────────
    if (pathname === '/Landing' || pathname === '/Catalogue' || pathname === '/EventManager' || pathname === '/v2' || pagesForActiveTab.length === 0) {
        return null;
    }

    // Color setup
    const selectedBg = theme.palette.primary.light;
    const selectedHover = theme.palette.primary.dark;
    const unselectedBg = isDark ? '#FFFFFF33' : '#FFFFFF66';
    const unselectedHover = isDark ? '#FFFFFF66' : '#FFFFFF33';
    const textColor = isDark ? '#fff' : '#000';
    const selectedText = '#000';

    const handlePageClick = (tile: any) => {
        setCurrentPage(tile.title);
        navigate(tile.navigateTo);
    };

    return (
        <Box sx={{ ml: 2, mt: 2, position: 'relative' }}>
            {/* Left Arrow */}
            {showLeft && (
                <Box
                    onClick={() => scrollByContainerWidth('left')}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: -10,
                        transform: 'translateY(-50%)',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#fff',
                        zIndex: 10,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                    }}
                >
                    <ArrowBackIosIcon sx={{ fontSize: 16, transform: 'translateX(2px)' }} />
                </Box>
            )}

            {/* Scrollable Container */}
            <Box
                ref={containerRef}
                onScroll={updateArrowsVisibility}
                sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    p: 1,
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                {pagesForActiveTab.map((tile) => (
                    <Box
                        key={tile.id}
                        onClick={() => handlePageClick(tile)}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: '20px',
                            backgroundColor:
                                tile.title === currentPage ? selectedBg : unselectedBg,
                            color:
                                tile.title === currentPage
                                    ? `${selectedText} !important`
                                    : textColor,
                            cursor: 'pointer',
                            flexShrink: 0,
                            textAlign: 'center',
                            boxShadow: 3,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                backgroundColor:
                                    tile.title === currentPage
                                        ? selectedHover
                                        : unselectedHover,
                            },
                        }}
                    >
                        {tile.title}
                    </Box>
                ))}
            </Box>

            {/* Right Arrow */}
            {showRight && (
                <Box
                    onClick={() => scrollByContainerWidth('right')}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 10,
                        transform: 'translateY(-50%)',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#fff',
                        zIndex: 10,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                    }}
                >
                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                </Box>
            )}
        </Box>
    );
};

export default CategoryTabs;